import { Booking, CustomerInfo, Location, Service } from '../types';

/**
 * Type-Safety Utilities für die Beauty Booking App
 */

/**
 * Sicherer Zugriff auf Customer-Informationen aus einem Booking-Objekt.
 */
export const getCustomerInfo = (booking: Booking): CustomerInfo => {
  if (booking.customer) {
    return booking.customer;
  }
  // Fallback, falls Customer-Daten nicht mitgeladen wurden.
  return {
    id: booking.customerId,
    name: 'Unbekannter Kunde',
    email: 'N/A',
    phone: 'N/A',
    address: 'N/A'
  };
};

/**
 * Validiert, ob ein Objekt die minimalen Eigenschaften für ein Booking hat.
 */
export const validateBooking = (booking: Partial<Booking>): booking is Booking => {
  return !!(
    booking.id &&
    booking.customerId &&
    booking.serviceId &&
    booking.locationId &&
    booking.bookingDate &&
    booking.bookingTime &&
    booking.status
  );
};

/**
 * Mappt ein snake_case Datenbank-Objekt auf ein camelCase Booking-Objekt für das Frontend.
 */
export const mapDatabaseBookingToBooking = (dbBooking: any): Booking => {
  return {
    id: dbBooking.id,
    customerId: dbBooking.customer_id,
    serviceId: dbBooking.service_id,
    locationId: dbBooking.location_id,
    employeeId: dbBooking.employee_id ?? undefined,
    bookingDate: dbBooking.booking_date,
    bookingTime: (dbBooking.booking_time ?? '').slice(0, 5),

    // Backend-Felder (snake_case) für Typkompatibilität
    customer_id: dbBooking.customer_id,
    service_id: dbBooking.service_id,
    location_id: dbBooking.location_id,
    employee_id: dbBooking.employee_id ?? undefined,
    booking_date: dbBooking.booking_date,
    booking_time: (dbBooking.booking_time ?? '').slice(0, 5),

    status: dbBooking.status,
    notes: dbBooking.notes || undefined,
    customer: dbBooking.customers || dbBooking.customer
      ? {
          id: (dbBooking.customers || dbBooking.customer).id,
          name: (dbBooking.customers || dbBooking.customer).name || 'Unbekannt',
          email: (dbBooking.customers || dbBooking.customer).email || 'keine@email.com',
          phone: (dbBooking.customers || dbBooking.customer).phone || 'Keine Nummer',
          address: (dbBooking.customers || dbBooking.customer).address || 'Keine Adresse',
        }
      : dbBooking.customer_name
      ? {
          id: dbBooking.customer_id,
          name: dbBooking.customer_name,
          email: dbBooking.customer_email || 'N/A',
          phone: dbBooking.customer_phone || 'N/A',
          address: dbBooking.customer_address || 'N/A',
        }
      : undefined,
    service: dbBooking.services || dbBooking.service
      ? (dbBooking.services || dbBooking.service)
      : dbBooking.service_id
      ? {
          id: dbBooking.service_id,
          name: dbBooking.service_name || `Service #${dbBooking.service_id}`,
          price: dbBooking.service_price || 0,
          duration: dbBooking.service_duration || 0,
          prepayment: dbBooking.needs_prepayment || false,
          category: dbBooking.service_category || 'Allgemein',
          location_ids: [],
          prepayment_amount: dbBooking.prepayment_amount || 0,
          description: '',
          created_at: dbBooking.created_at,
          updated_at: dbBooking.updated_at,
        }
      : undefined,
    location: dbBooking.locations || dbBooking.location || undefined,
    employee: dbBooking.employees || dbBooking.employee || undefined,
    needsPrepayment: !!dbBooking.needs_prepayment,
    prepaymentAmount: dbBooking.prepayment_amount || 0,
    created: dbBooking.created_at || new Date().toISOString(),
    reminderSent: !!dbBooking.reminder_sent,
    paymentMethod: dbBooking.payment_method || undefined
  };
};

/**
 * Mappt ein camelCase Booking-Objekt vom Frontend in ein snake_case Objekt für die Datenbank.
 */
export const mapBookingToDatabase = (booking: Partial<Booking>): any => {
  const dbBooking: { [key: string]: any } = {};
  if (booking.id !== undefined) dbBooking.id = booking.id;
  if (booking.customerId !== undefined) dbBooking.customer_id = booking.customerId;
  if (booking.serviceId !== undefined) dbBooking.service_id = booking.serviceId;
  if (booking.locationId !== undefined) dbBooking.location_id = booking.locationId;
  if (booking.employeeId !== undefined) dbBooking.employee_id = booking.employeeId;
  if (booking.bookingDate !== undefined) dbBooking.booking_date = booking.bookingDate;
  if (booking.bookingTime !== undefined) dbBooking.booking_time = booking.bookingTime;
  if (booking.status !== undefined) dbBooking.status = booking.status;
  if (booking.notes !== undefined) dbBooking.notes = booking.notes;
  if (booking.needsPrepayment !== undefined) dbBooking.needs_prepayment = booking.needsPrepayment;
  if (booking.prepaymentAmount !== undefined) dbBooking.prepayment_amount = booking.prepaymentAmount;
  if (booking.reminderSent !== undefined) dbBooking.reminder_sent = booking.reminderSent;
  if (booking.paymentMethod !== undefined) dbBooking.payment_method = booking.paymentMethod;
  // 'created' wird normalerweise von der DB gesetzt
  return dbBooking;
};

export const convertLocation = (loc: any): Location => {
  if (loc.openTimes || loc.individualOpenTimes) {
    return loc as Location;
  }

  let individualOpenTimes: any = loc.individual_open_times;
  if (typeof individualOpenTimes === 'string') {
    try {
      individualOpenTimes = JSON.parse(individualOpenTimes);
    } catch {
      individualOpenTimes = undefined;
    }
  }

  return {
    id: Number(loc.id),
    name: loc.name,
    address: loc.address,
    phone: loc.phone,
    email: loc.email,
    active: loc.active,
    city: '',
    postalCode: '',
    individualOpenTimes: individualOpenTimes,
    openingHours: {
      monday: { start: loc.open_times.start, end: loc.open_times.end, closed: !loc.open_days.includes(1) },
      tuesday: { start: loc.open_times.start, end: loc.open_times.end, closed: !loc.open_days.includes(2) },
      wednesday: { start: loc.open_times.start, end: loc.open_times.end, closed: !loc.open_days.includes(3) },
      thursday: { start: loc.open_times.start, end: loc.open_times.end, closed: !loc.open_days.includes(4) },
      friday: { start: loc.open_times.start, end: loc.open_times.end, closed: !loc.open_days.includes(5) },
      saturday: { start: loc.open_times.start, end: loc.open_times.end, closed: !loc.open_days.includes(6) },
      sunday: { start: loc.open_times.start, end: loc.open_times.end, closed: !loc.open_days.includes(0) }
    },
    createdAt: loc.created_at,
    updatedAt: loc.updated_at,
    openDays: loc.open_days,
    openTimes: loc.open_times,
    slotDuration: loc.slot_duration,
    bufferTime: loc.buffer_time,
    lunchWindowStart: loc.lunch_break_start ?? (loc as any).lunch_window_start,
    lunchWindowEnd:   loc.lunch_break_end   ?? (loc as any).lunch_window_end,
    lunchBreakDuration: loc.lunch_break_duration,

    // Backend-kompatible snake_case Felder (für Typ-Gültigkeit)
    created_at: loc.created_at ?? loc.createdAt ?? new Date().toISOString(),
    updated_at: loc.updated_at ?? loc.updatedAt ?? new Date().toISOString(),
    open_days: loc.open_days ?? [],
    open_times: loc.open_times,
    slot_duration: loc.slot_duration,
  };
};

export const convertService = (svc: any): Service => ({
  id: svc.id,
  name: svc.name,
  duration: svc.duration,
  price: svc.price,
  prepayment: svc.prepayment,
  description: svc.description || '',
  category: svc.category,
  locationIds: svc.location_ids,
  createdAt: svc.created_at,
  updatedAt: svc.updated_at,
  prepaymentAmount: svc.prepayment_amount,

  // snake_case Felder für Backend-Kompatibilität
  prepayment_amount: svc.prepayment_amount,
  location_ids: svc.location_ids,
  created_at: svc.created_at,
  updated_at: svc.updated_at,
});
